// src/pages/AdminPage/tabs/TopicsTab.tsx
import { useCallback } from "react";
import { Folder, Edit2, Trash2, Plus } from "lucide-react";
import { adminAPI } from "../utils/adminApi";
import { usePaginatedData } from "../hooks/usePaginatedData";
import SearchBar from "../components/SearchBar";
import DataTable, { Column } from "../components/DataTable";
import Pagination from "../components/Pagination";
import type { TopicItem } from "../utils/adminTypes";

interface TopicsTabProps {
  onCreate: () => void;
  onEdit: (topic: TopicItem) => void;
  onDelete: (id: number) => void;
  onError: (msg: string) => void;
}

export default function TopicsTab({
  onCreate,
  onEdit,
  onDelete,
  onError,
}: TopicsTabProps) {
  const fetcher = useCallback(
    (page: number, limit: number, search: string) =>
      adminAPI.getTopics(page, limit, search),
    [],
  );

  const {
    data: topics,
    total,
    page,
    setPage,
    search,
    setSearch,
    loading,
  } = usePaginatedData<TopicItem>({
    fetcher,
    limit: 20,
    onError,
  });

  const columns: Column<TopicItem>[] = [
    {
      key: "id",
      label: "#",
      render: (t) => (
        <span style={{ color: "#475569", fontSize: 12, whiteSpace: "nowrap" }}>
          #{t.id}
        </span>
      ),
    },
    {
      key: "name",
      label: "Mavzu nomi",
      render: (t) => (
        <div>
          <div style={{ color: "#E2E8F0", fontWeight: 600, fontSize: 13 }}>
            {t.name_oz || "—"}
          </div>
          <div style={{ color: "#475569", fontSize: 11, marginTop: 2 }}>
            {t.name_ru || "—"}
          </div>
        </div>
      ),
    },
    {
      key: "description",
      label: "Tavsif",
      hideOnMobile: true,
      render: (t) => (
        <p
          style={{
            color: "#64748B",
            fontSize: 12,
            margin: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: 250,
          }}>
          {t.description_oz || "—"}
        </p>
      ),
    },
    {
      key: "questions",
      label: "Savollar",
      render: (t) => (
        <span
          style={{
            padding: "3px 10px",
            borderRadius: 99,
            fontSize: 11,
            fontWeight: 700,
            background: "rgba(139,92,246,.15)",
            color: "#8B5CF6",
          }}>
          {t.question_count ?? 0} ta
        </span>
      ),
    },
    {
      key: "actions",
      label: "Amallar",
      render: (t) => (
        <div style={{ display: "flex", gap: 6 }}>
          <button
            onClick={() => onEdit(t)}
            title="Tahrirlash"
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              border: "1px solid rgba(59,130,246,.3)",
              background: "rgba(59,130,246,.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}>
            <Edit2 size={12} color="#3B82F6" />
          </button>
          <button
            onClick={() => onDelete(t.id)}
            title="O'chirish"
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              border: "1px solid rgba(239,68,68,.3)",
              background: "rgba(239,68,68,.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}>
            <Trash2 size={12} color="#EF4444" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="fade-up">
      <div
        style={{
          display: "flex",
          gap: 10,
          marginBottom: 16,
          flexWrap: "wrap",
        }}>
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Mavzu nomi bo'yicha qidirish..."
        />
        <button
          onClick={onCreate}
          className="btn-hover"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "10px 16px",
            borderRadius: 12,
            border: "none",
            background: "#8B5CF6",
            color: "#fff",
            fontWeight: 700,
            fontSize: 13,
            cursor: "pointer",
          }}>
          <Plus size={15} /> Yangi mavzu
        </button>
        <div
          style={{
            color: "#64748B",
            fontSize: 13,
            padding: "10px 14px",
            background: "#111827",
            border: "1px solid #1E2D45",
            borderRadius: 12,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}>
          <Folder size={14} /> Jami:{" "}
          <strong style={{ color: "#fff" }}>{total}</strong>
        </div>
      </div>

      <DataTable<TopicItem>
        columns={columns}
        data={topics}
        loading={loading}
        rowKey={(t) => t.id}
        emptyMessage="Mavzular topilmadi"
      />

      <Pagination page={page} total={total} limit={20} onChange={setPage} />
    </div>
  );
}
